import { supabase } from './supabase'

/**
 * Get nutrition therapy for a care plan
 */
export const getNutritionTherapy = async (carePlanId, organizationId) => {
  try {
    const { data, error } = await supabase
      .from('nutrition_therapy')
      .select('*')
      .eq('care_plan_id', carePlanId)
      .eq('organization_id', organizationId)
      .single()

    if (error && error.code !== 'PGRST116') throw error
    return data || null
  } catch (error) {
    console.error('Error fetching nutrition therapy:', error)
    throw error
  }
}

/**
 * Create or update nutrition therapy
 */
export const saveNutritionTherapy = async (therapyData) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    const payload = {
      organization_id: therapyData.organizationId,
      care_plan_id: therapyData.carePlanId,
      client_id: therapyData.clientId,
      effects_to_avoid: therapyData.effectsToAvoid || [],
      desired_food_effects: therapyData.desiredFoodEffects || [],
      desired_herb_effects: therapyData.desiredHerbEffects || [],
      drug_herb_interactions: therapyData.drugHerbInteractions || [],
      general_notes: therapyData.generalNotes || null,
      updated_at: new Date().toISOString(),
    }

    // Check if exists
    const { data: existing } = await supabase
      .from('nutrition_therapy')
      .select('id')
      .eq('care_plan_id', therapyData.carePlanId)
      .single()

    if (existing) {
      // Update
      const { data, error } = await supabase
        .from('nutrition_therapy')
        .update(payload)
        .eq('id', existing.id)
        .select()
        .single()

      if (error) throw error
      return data
    } else {
      // Insert
      const { data, error } = await supabase
        .from('nutrition_therapy')
        .insert({
          ...payload,
          created_by: user?.id,
        })
        .select()
        .single()

      if (error) throw error
      return data
    }
  } catch (error) {
    console.error('Error saving nutrition therapy:', error)
    throw error
  }
}

/**
 * Check drug-herb interactions for a client
 */
export const checkDrugHerbInteractions = async (clientId, organizationId, herbs) => {
  try {
    const { data, error } = await supabase.rpc('check_drug_herb_interactions', {
      client_id_param: clientId,
      org_id_param: organizationId,
      herbs_list: herbs,
    })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error checking drug-herb interactions:', error)
    throw error
  }
}

/**
 * Get client health data (allergies, medications, etc.)
 */
export const getClientHealthData = async (clientId, organizationId) => {
  try {
    const { data, error } = await supabase.rpc('get_client_health_data', {
      client_id_param: clientId,
      org_id_param: organizationId,
    })

    if (error) throw error
    return data || {}
  } catch (error) {
    console.error('Error fetching client health data:', error)
    throw error
  }
}

/**
 * Search drug-herb interactions database
 */
export const searchDrugHerbInteractions = async (medicationName = null, herbName = null) => {
  try {
    let query = supabase.from('drug_herb_interactions').select('*')

    if (medicationName) {
      query = query.ilike('medication_name', `%${medicationName}%`)
    }
    if (herbName) {
      query = query.ilike('herb_name', `%${herbName}%`)
    }

    const { data, error } = await query.order('severity', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error searching drug-herb interactions:', error)
    throw error
  }
}

